import { Combobox, Transition } from "@headlessui/react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconFilter,
  IconSearch,
  IconSearchOff,
} from "@tabler/icons-react";
import clsx from "clsx";
import Checkbox from "components/Checkbox";
import DebouncedInput from "components/DebounceInput";
import Tooltip from "components/Tooltip";
import { observer } from "mobx-react-lite";
import React, { ReactNode, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { globalStore } from "store/GlobalStore";

export interface DropdownOption {
  id?: string | number;
  name?: string | null;
  name_de?: string | null;
  icon?: ReactNode;
  title?: string;
  title_de?: string;
  value?: string;
  children?: DropdownOption[];
  hasChildren?: boolean;
  badge?: string;
  disabled?: boolean;
}

interface ChildrenOptions {
  count: number;
  items: DropdownOption[];
}

interface FilterMenuProps {
  options: DropdownOption[];
  wrapperID?: string;
  fetchChildren?: (
    id: string,
    searchTerm: string,
    pageNumber: number,
    pageSize: number,
    update?: boolean
  ) => Promise<ChildrenOptions | undefined>;
  onSelectItem?: (items: SelectedItems[]) => void;
  checkedItems?: SelectedItems[];
  pageSize?: number;
  noResultText?: string;
  disabled?: boolean;
  singleSelect?: boolean;
}

export interface SelectedItems {
  category: DropdownOption;
  items: DropdownOption[];
}

const FiltersMenu = ({ pageSize = 25, ...props }: FilterMenuProps) => {
  const { t } = useTranslation();
  const wrapperEl = document.getElementById(props.wrapperID as string);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [hasMoreOptions, setHasMoreOptions] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const isEnglish = globalStore.currentLanguage === "en";
  const [childrenOptions, setChildrenOptions] = React.useState<ChildrenOptions>(
    { count: 0, items: [] }
  );
  const [selectedItems, setSelectedItems] = React.useState<SelectedItems[]>([]);

  const [parentCategory, setParentCategory] =
    React.useState<DropdownOption | null>();

  React.useEffect(() => {
    props.checkedItems && setSelectedItems(props.checkedItems);
  }, [props.checkedItems]);

  React.useEffect(() => {
    return () => clearStates();
  }, [isMenuOpen]);

  React.useEffect(() => {
    onChangeQuery();
  }, [searchTerm]);

  function clearStates() {
    setParentCategory(null);
    setChildrenOptions({ count: 0, items: [] });
  }

  async function onChangeQuery() {
    if (!parentCategory?.id) return;
    setLoading(true);
    await getChildrenOptions?.(parentCategory, 1, true);
    setLoading(false);
  }

  async function onClickParent(item: DropdownOption) {
    if (!item.id) return;
    setLoading(true);
    setParentCategory(item);
    await getChildrenOptions(item, 1);
    setLoading(false);
  }

  async function getChildrenOptions(
    parent: DropdownOption,
    pageNumber?: number,
    update?: boolean
  ) {
    const res = await props.fetchChildren?.(
      String(parent.id),
      searchTerm,
      pageNumber ?? childrenOptions.items.length / pageSize + 1,
      pageSize
    );
    if (!res) return;
    const itemsList = update
      ? res?.items
      : [...childrenOptions.items, ...res.items];

    setChildrenOptions({
      count: res.count,
      items: itemsList,
    });
    setHasMoreOptions(itemsList.length !== res.count);
  }

  function onClickChild(clickedItem: DropdownOption, state: boolean) {
    let currentSelectList = [...selectedItems];
    const selectedCategory = currentSelectList.find(
      (rec) => rec.category.id === parentCategory?.id
    );
    if (state && parentCategory?.id) {
      if (selectedCategory) {
        if (props.singleSelect) {
          selectedCategory.items = [clickedItem];
        } else {
          selectedCategory.items.push(clickedItem);
        }
      } else {
        currentSelectList.push({
          category: parentCategory,
          items: [clickedItem],
        });
      }
    } else if (!state && selectedCategory) {
      const filteredItems = selectedCategory?.items.filter(
        (rec) => String(rec.id) !== String(clickedItem.id)
      );
      if (filteredItems.length) {
        selectedCategory.items = filteredItems;
      } else {
        currentSelectList = currentSelectList.filter(
          (rec) => rec.category?.id !== parentCategory?.id
        );
      }
    }
    props.onSelectItem?.([...currentSelectList]);
  }

  function searchField() {
    return (
      <div className="sticky top-0 p-4 z-10 bg-white">
        <div
          className={clsx(
            "bg-white border-gray-300 border rounded-md",
            "flex justify-between px-2 items-center text-gray-900",
            {
              "opacity-50 !cursor-not-allowed pointer-events-none":
                props.disabled,
            }
          )}
        >
          <DebouncedInput
            className={clsx(
              "w-full pl-0 placeholder:text-gray-500 text-sm leading-5 ring-0 outline-none border-none focus:ring-0 rounded-md"
            )}
            inputWrapperClassName="border-none"
            onChange={(value) => setSearchTerm(String(value))}
            value={searchTerm}
            placeholder={t("commons.search")}
            icon={<></>}
          />
          <IconSearch width={24} className="text-gray-500" />
        </div>
      </div>
    );
  }

  function onClickBack(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation();
    setParentCategory(null);
    setSearchTerm("");
    setChildrenOptions({ count: 0, items: [] });
  }

  async function onScrollLoadMore(e: React.UIEvent<HTMLDivElement, UIEvent>) {
    e.stopPropagation();
    if (!hasMoreOptions || !parentCategory?.id) return;
    if (e.target) {
      const { scrollHeight, scrollTop, clientHeight } =
        e.target as HTMLDivElement;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        await getChildrenOptions(parentCategory);
      }
    }
  }

  function hasCategoryAnySelection(categoryID: string | number | undefined) {
    const currentCategory = selectedItems?.find(
      (rec) => rec.category?.id === categoryID
    );
    return currentCategory ?? false;
  }

  function isItemChecked(item: DropdownOption) {
    return (
      selectedItems
        .find((rec) => rec.category?.id === parentCategory?.id)
        ?.items.map((rec) => String(rec.id))
        ?.includes(String(item.id)) ?? false
    );
  }

  function comboOption(item: DropdownOption) {
    return item.hasChildren ? parentItem() : childItem();

    function parentItem(): React.ReactElement {
      return (
        <Combobox.Option
          key={item.id}
          className={clsx(
            "text-gray-700 cursor-pointer min-w-[300px]",
            "hover:bg-indigo-50 hover:text-indigo-700",
            "flex justify-between items-center gap-6 text-sm font-medium px-4 py-2",
            {
              "!opacity-50 pointer-events-none cursor-not-allowed":
                item.disabled,
            }
          )}
          value={item}
          onClick={() => onClickParent(item)}
          disabled={item.disabled}
        >
          <span>{isEnglish ? item.name : item.name_de}</span>
          <div className="flex items-center">
            {hasCategoryAnySelection(item.id) && (
              <span className="text-gray-500 pr-2 font-normal text-nowrap">
                {t("commons.filterSet")}
              </span>
            )}
            <IconChevronRight size={20} />
          </div>
        </Combobox.Option>
      );
    }

    function childItem(): React.ReactElement {
      return (
        <label
          key={item.id}
          className={clsx(
            "flex justify-between items-center text-sm font-medium px-4 py-2",
            "hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer",
            {
              "opacity-50 !cursor-not-allowed pointer-events-none":
                props.disabled,
            }
          )}
          htmlFor={item.id as string}
        >
          <div className="flex">
            <Checkbox
              id={item.id as string}
              onChange={(e) => onClickChild(item, e.currentTarget.checked)}
              containerClassName="!py-0 mr-3"
              checked={isItemChecked(item)}
              disabled={props.disabled}
            />
            <Tooltip
              content={isEnglish ? item.name : item.name_de}
              truncate
              className="max-w-[230px]"
            >
              {isEnglish ? item.name : item.name_de}
            </Tooltip>
          </div>
          {item.badge == "HIGH_QUALITY" && (
            <div className="text-xs rounded-full px-2 mr-1 bg-green-100 text-green-700 whitespace-nowrap">
              {t("productEditor.highDataQuality")}
            </div>
          )}
        </label>
      );
    }
  }

  function noSearchResult() {
    return (
      <div className="flex flex-col items-center justify-center w-full h-[300px]">
        <IconSearchOff size={57} className="text-gray-200 mb-4" />
        <div className="text-gray-500 text-xs font-medium text-center">
          {props.noResultText}
        </div>
      </div>
    );
  }

  function showOptions() {
    if (parentCategory && childrenOptions.items.length === 0) {
      return noSearchResult();
    }
    const currentList = parentCategory ? childrenOptions.items : props.options;
    return currentList.map((item) => comboOption(item));
  }

  function menuContent(): React.ReactNode {
    return (
      <Transition
        as={"div"}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className={clsx(
            "absolute z-20 my-1 w-96 right-0 min-w-[300px] overflow-hidden",
            "rounded-md bg-white text-base !p-0",
            "shadow-lg border border-gray-300 min-w-auto w-auto max-w-md focus:outline-none sm:text-sm"
          )}
        >
          <Combobox.Options>
            {parentCategory && (
              <div className="sticky top-0">
                {searchField()}
                <div
                  className={clsx(
                    "text-gray-500 flex items-center pl-2 pr-4 py-[6px] border-y border-gray-300",
                    "cursor-pointer bg-gray-50 group/menu relative"
                  )}
                  onClick={(e) => onClickBack(e)}
                >
                  <IconChevronLeft
                    size={24}
                    className="group-hover/menu:text-indigo-700"
                  />
                  <div className="text-center text-sm font-semibold w-full">
                    {isEnglish ? parentCategory?.name : parentCategory?.name_de}{" "}
                    {t("productEditor.isTheSame")}
                  </div>
                </div>
              </div>
            )}

            <div
              className={clsx(
                "overflow-auto",
                parentCategory ? "max-h-[280px]" : "max-h-[420px]"
              )}
              onScroll={onScrollLoadMore}
            >
              {loading ? skeleton() : showOptions()}
            </div>

            {parentCategory && hasMoreOptions && !loading && (
              <div className="py-1 bg-white px-4 text-gray-500 border-t border-gray-300 sticky bottom-0 w-full">
                {t("commons.scrollToLoadMore")}
              </div>
            )}
          </Combobox.Options>
        </div>
      </Transition>
    );
  }

  return (
    <div className={clsx("rounded-md")}>
      <Combobox multiple>
        <Combobox.Button>
          {({ open }) => {
            useEffect(() => {
              setIsMenuOpen(open);
            }, [open]);
            return (
              <div
                className={clsx(
                  "border border-gray-300 rounded-md flex justify-between h-10 items-center px-2",
                  "text-base text-gray-700 font-medium cursor-pointer"
                )}
              >
                <IconFilter className="mr-2" />
                {t("commons.filter")}
              </div>
            );
          }}
        </Combobox.Button>
        {wrapperEl
          ? ReactDOM.createPortal(menuContent(), wrapperEl)
          : menuContent()}
      </Combobox>
    </div>
  );
};

export default observer(FiltersMenu);

function skeleton() {
  const divs = new Array(9)
    .fill(null)
    .map((_, index) => (
      <div
        key={index}
        className="h-7 w-full bg-gray-200 rounded-lg dark:bg-gray-700 my-1"
      ></div>
    ));

  return <div className="animate-pulse px-4 py-2">{divs}</div>;
}
